import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useProductBundles, calculateBundleSavings } from '@/hooks/useProductBundles';
import { useBundleConversionStats } from '@/hooks/useBundleConversion';
import { Scale, TrendingUp, DollarSign, Package, Check, X } from 'lucide-react';

interface BundleComparisonToolProps {
  onSelectBundle?: (bundleId: string) => void;
}

export function BundleComparisonTool({ onSelectBundle }: BundleComparisonToolProps) {
  const { data: bundles, isLoading: bundlesLoading } = useProductBundles();
  const { data: conversionStats, isLoading: statsLoading } = useBundleConversionStats();
  const [selectedBundles, setSelectedBundles] = useState<string[]>([]);

  const isLoading = bundlesLoading || statsLoading;

  const activeBundles = bundles?.filter(b => b.is_active) || [];

  const toggleBundle = (bundleId: string) => {
    setSelectedBundles(prev => {
      if (prev.includes(bundleId)) {
        return prev.filter(id => id !== bundleId);
      }
      if (prev.length >= 3) {
        return [...prev.slice(1), bundleId];
      }
      return [...prev, bundleId];
    });
  };

  const getConversionData = (bundleId: string) => {
    return conversionStats?.find(s => s.bundle_id === bundleId);
  };

  const comparedBundles = activeBundles.filter(b => selectedBundles.includes(b.id));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="h-5 w-5" />
          Bundle Comparison Tool
        </CardTitle>
        <CardDescription>Select up to 3 bundles to compare side-by-side</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Bundle Selection */}
        <div>
          <p className="text-sm font-medium mb-3">Select Bundles to Compare</p>
          <ScrollArea className="h-[150px] border rounded-lg p-3">
            <div className="space-y-2">
              {activeBundles.map(bundle => {
                const savings = calculateBundleSavings(bundle);
                const isSelected = selectedBundles.includes(bundle.id);
                
                return (
                  <div
                    key={bundle.id}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => toggleBundle(bundle.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox checked={isSelected} />
                      <div>
                        <p className="font-medium">{bundle.name}</p>
                        <p className="text-xs text-muted-foreground">
                          ${savings.bundlePrice.toLocaleString()} · {bundle.items?.length || 0} products
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary">{savings.savingsPercent.toFixed(0)}% off</Badge>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Comparison Table */}
        {comparedBundles.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium text-muted-foreground">Metric</th>
                    {comparedBundles.map(bundle => (
                      <th key={bundle.id} className="text-center p-3 font-medium min-w-[150px]">
                        {bundle.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {/* Price Row */}
                  <tr>
                    <td className="p-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      Bundle Price
                    </td>
                    {comparedBundles.map(bundle => {
                      const savings = calculateBundleSavings(bundle);
                      return (
                        <td key={bundle.id} className="p-3 text-center">
                          <p className="font-semibold">${savings.bundlePrice.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground line-through">
                            ${savings.originalPrice.toLocaleString()}
                          </p>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Savings Row */}
                  <tr>
                    <td className="p-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      Savings
                    </td>
                    {comparedBundles.map(bundle => {
                      const savings = calculateBundleSavings(bundle);
                      return (
                        <td key={bundle.id} className="p-3 text-center">
                          <Badge variant="default" className="bg-chart-1">
                            {savings.savingsPercent.toFixed(0)}% off
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            Save ${savings.savings.toLocaleString()}
                          </p>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Products Row */}
                  <tr>
                    <td className="p-3 flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      Products Included
                    </td>
                    {comparedBundles.map(bundle => (
                      <td key={bundle.id} className="p-3 text-center">
                        <p className="font-medium">{bundle.items?.length || 0} products</p>
                      </td>
                    ))}
                  </tr>

                  {/* Conversion Rate Row */}
                  <tr>
                    <td className="p-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      Win Rate
                    </td>
                    {comparedBundles.map(bundle => {
                      const conversion = getConversionData(bundle.id);
                      return (
                        <td key={bundle.id} className="p-3 text-center">
                          {conversion ? (
                            <>
                              <p className="font-semibold">{conversion.conversion_rate.toFixed(1)}%</p>
                              <p className="text-xs text-muted-foreground">
                                {conversion.won_deals}/{conversion.won_deals + conversion.lost_deals} won
                              </p>
                            </>
                          ) : (
                            <span className="text-muted-foreground">No data</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Won Revenue Row */}
                  <tr>
                    <td className="p-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      Won Revenue
                    </td>
                    {comparedBundles.map(bundle => {
                      const conversion = getConversionData(bundle.id);
                      return (
                        <td key={bundle.id} className="p-3 text-center">
                          {conversion ? (
                            <p className="font-semibold">${conversion.total_won_revenue.toLocaleString()}</p>
                          ) : (
                            <span className="text-muted-foreground">$0</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Product Details */}
            <div className="border-t p-4 bg-muted/30">
              <p className="text-sm font-medium mb-3">Included Products</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {comparedBundles.map(bundle => (
                  <div key={bundle.id} className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">{bundle.name}</p>
                    <ul className="space-y-1">
                      {bundle.items?.map(item => (
                        <li key={item.id} className="text-sm flex items-center gap-2">
                          <Check className="h-3 w-3 text-chart-1" />
                          {item.product?.name} {item.quantity > 1 && `(x${item.quantity})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            {onSelectBundle && (
              <div className="border-t p-4 flex gap-2 justify-end">
                {comparedBundles.map(bundle => (
                  <Button
                    key={bundle.id}
                    variant="outline"
                    size="sm"
                    onClick={() => onSelectBundle(bundle.id)}
                  >
                    Select {bundle.name}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {comparedBundles.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Scale className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Select bundles above to compare them</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
